/**
 * pingo-wheel-gate — decisor LEVE da roleta (carrega em TODA página da loja).
 *
 * Avalia a MESMA regra do motor (isLimited + rollDisplay + gatilho) sem baixar
 * nem EXECUTAR o motor pesado (pingo-wheel.js): disco, stickers, confete e todo
 * o connectedCallback só rodam quando o gatilho bate.
 *
 * O PAYLOAD (modal + fatias + form) fica VIVO no DOM desde o load, então o form
 * do app Shopify Forms hidrata pelo caminho normal (o app varre [data-form-root]
 * UMA vez no load; um form injetado depois NÃO hidrata de forma confiável — por
 * isso o form NÃO é adiado). No gatilho o gate só marca data-pw-fire e importa
 * o motor (data-pw-engine), que constrói a roda e abre.
 *
 * As regras leem os MESMOS data-* que o motor lê — vêm de section.settings.*,
 * então mudar “Probabilidade de exibição”, limite, gatilho, etc. no editor
 * continua valendo, sem tocar neste arquivo.
 *
 * Só no storefront: no editor o motor carrega ansiosamente (ver pingo-wheel.liquid).
 * As chaves de storage têm o mesmo PREFIX do motor, então gate e motor
 * compartilham `roll` / `dismissed` / `spun` / `spun-at` / `dismissed-at`.
 */
(() => {
  const PREFIX = 'pingo-wheel';

  const store = {
    get(area, key) {
      try {
        return window[area].getItem(`${PREFIX}:${key}`);
      } catch (error) {
        return null;
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(`${PREFIX}:${key}`, value);
      } catch (error) {
        /* modo privado / storage cheio — degrada para "sem memória" */
      }
    },
  };

  const arm = (el) => {
    if (el.dataset.pwGateReady) return;
    el.dataset.pwGateReady = 'true';

    // — regra (espelha shouldOpen/isLimited/rollDisplay do motor) —
    const isLimited = () => {
      if (store.get('sessionStorage', 'dismissed') === '1') return true;
      const scope = el.dataset.limitScope;
      if (scope === 'none') return false;
      if (scope === 'session') return store.get('sessionStorage', 'spun') === '1';
      const spunAt = Number(store.get('localStorage', 'spun-at')) || 0;
      const dismissedAt = Number(store.get('localStorage', 'dismissed-at')) || 0;
      const lastAt = Math.max(spunAt, dismissedAt);
      if (!lastAt) return false;
      if (scope === 'browser') return true;
      const days = parseInt(el.dataset.limitDays, 10) || 7;
      return Date.now() - lastAt < days * 864e5;
    };

    const rollDisplay = () => {
      const chance = parseInt(el.dataset.displayChance, 10);
      if (chance >= 100) return true;
      const remembered = store.get('sessionStorage', 'roll');
      if (remembered !== null) return remembered === '1';
      const won = Math.random() * 100 < chance;
      store.set('sessionStorage', 'roll', won ? '1' : '0');
      return won;
    };

    const isTest = el.dataset.testEnabled === 'true';
    const shouldOpen = isTest || (!isLimited() && rollDisplay());
    if (!shouldOpen) return; // 75%+ das visitas param aqui: nada pesado carrega.

    // — disparo: clona o payload, marca fire, importa o motor —
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      // Payload já está vivo (o form do app hidratou no load). O gate só adia o
      // MOTOR: marca fire e importa pingo-wheel.js, que constrói a roda e abre.
      el.dataset.pwFire = '1';
      if (!window.__pingoWheelEngine) {
        window.__pingoWheelEngine = true;
        const script = document.createElement('script');
        script.src = el.dataset.pwEngine;
        script.defer = true;
        document.head.appendChild(script);
      }
    };

    // — gatilho (espelha scheduleOpen do motor) —
    if (isTest) return fire();
    const trigger = el.dataset.trigger;

    if (trigger === 'scroll') {
      const target = parseInt(el.dataset.scrollPct, 10) || 40;
      const wrapper = document.querySelector('.page-wrapper');
      const cleanup = () => {
        window.removeEventListener('scroll', onScroll);
        wrapper?.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      };
      const onScroll = () => {
        // Horizon scrolls the wrapper on desktop, the document below 990px.
        const scroller = wrapper && window.matchMedia('(min-width: 990px)').matches
          ? wrapper : document.scrollingElement;
        const max = scroller.scrollHeight - scroller.clientHeight;
        if (max > 0 && (scroller.scrollTop / max) * 100 >= target) {
          cleanup();
          fire();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      wrapper?.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();
      return;
    }

    if (trigger === 'exit') {
      const onLeave = (event) => {
        if (event.clientY > 0) return;
        document.removeEventListener('mouseout', onLeave);
        fire();
      };
      document.addEventListener('mouseout', onLeave);
      return;
    }

    // O "Atraso" do editor conta a partir do INÍCIO DA NAVEGAÇÃO, não de quando
    // este gate acorda. O gate é `defer`: ele só executa depois do HTML/CSS, o
    // que no telemóvel custa 1–3 s — então um ajuste de 5 s abria a roleta aos
    // ~7–9 s reais (medido: gate armado em 1,0 s, disparo em 6,16 s num desktop
    // local, e pior em 4G). O lojista ajusta o tempo que o VISITANTE sente.
    // `performance.now()` já é ms desde o início da navegação; se o gate acordar
    // depois do prazo, dispara imediatamente em vez de somar mais espera.
    const delay = (parseInt(el.dataset.delay, 10) || 0) * 1000;
    window.setTimeout(fire, Math.max(0, delay - performance.now()));
  };

  document.querySelectorAll('pingo-wheel[data-pw-gate]').forEach(arm);
})();
